import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CProgress,
  CProgressBar,
  CButton,
  CTooltip,
} from "@coreui/react";
import { toast } from "react-toastify";
import { CIcon } from "@coreui/icons-react";
import {
  cilChevronRight,
  cilChevronBottom,
  cilImagePlus,
  cilDescription,
  cilBorderOuter,
  cilTrash,
  cilSettings,
  cilViewQuilt,
  cilReload,
  cilArrowLeft,
  cilArrowRight,
  cilZoomIn,
  cilZoomOut,
  cilZoom,
  cilImage,
  cilMovie,
  cilSpeech,
} from "@coreui/icons";
import SettingsNeuralModule from "./SettingsNeuralModule";
import DefectPropertiesPanel from "./DefectPropertiesPanel";
import AddDefectsModal from "./AddDefectsModal";
import ReportNeuralModule from "./ReportNeuralModule";
import "./MainNeuralModal.scss";
import "../Button/Button.scss";

const isDarkColor = (color) => {
  if (!color) return true;
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  } else if (color.startsWith("rgb")) {
    const match = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness < 128;
    }
  }
  return true;
};

const extractDefectName = (fullDefectString) => {
  if (typeof fullDefectString !== "string") return "Неизвестный дефект";
  const parts = fullDefectString.split(" - ");
  return parts.length > 1 ? parts.slice(1).join(" - ") : parts[0];
};

const MainNeuralModal = ({ visible, onClose, objectData }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [fullImageSrc, setFullImageSrc] = useState(null);
  const [fullImageLoading, setFullImageLoading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1); // Состояние масштаба
  const [firstLevelItems, setFirstLevelItems] = useState([]);
  const [secondLevelItems, setSecondLevelItems] = useState({});
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [treeLoading, setTreeLoading] = useState(false);
  const [allFirstLevelClasses, setAllFirstLevelClasses] = useState([]);
  const [allSecondLevelClasses, setAllSecondLevelClasses] = useState([]);
  const [selectedFirstLevelClass, setSelectedFirstLevelClass] = useState("");
  const [selectedSecondLevelClass, setSelectedSecondLevelClass] = useState("");
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [addDefectsModalVisible, setAddDefectsModalVisible] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBottomPanel, setShowBottomPanel] = useState(true);
  const [leftPanelWidth] = useState(600);
  const [annotations, setAnnotations] = useState([]);
  const [additionalAnnotations, setAdditionalAnnotations] = useState([]);
  const [annotationsLoading, setAnnotationsLoading] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showAdditionalAnnotations, setShowAdditionalAnnotations] =
    useState(true);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tempBox, setTempBox] = useState(null);
  const [savedBoxes, setSavedBoxes] = useState([]);
  const [draggingBox, setDraggingBox] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingBox, setResizingBox] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [coordinatesForPropertiesPanel, setCoordinatesForPropertiesPanel] =
    useState(null);
  const [apiDataForPropertiesPanel, setApiDataForPropertiesPanel] =
    useState(null);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const zoomContainerRef = useRef(null); // Ref для контейнера с зумом

  const [imageDisplaySize, setImageDisplaySize] = useState({
    width: 0,
    height: 0,
  });
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  });
  const [currentDefectName, setCurrentDefectName] = useState("");
  const [markerColor, setMarkerColor] = useState("#FF0000");
  const token = localStorage.getItem("token");
  const safeOnClose = typeof onClose === "function" ? onClose : () => {};

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateError, setRotateError] = useState(null);

  const [drawStartPos, setDrawStartPos] = useState(null);
  const [settingsMode, setSettingsMode] = useState("full");

  const expandedItemsRef = useRef(expandedItems);
  useEffect(() => {
    expandedItemsRef.current = expandedItems;
  }, [expandedItems]);

  // ОБРАБОТЧИК ЗУМА КОЛЕСИКОМ МЫШИ С CTRL
  useEffect(() => {
    // Включаем перехват только если модалка открыта
    if (!visible) return;

    const handleWheel = (e) => {
      if (e.ctrlKey) {
        // Обязательно отключаем стандартное поведение браузера
        e.preventDefault(); 
        
        const zoomOut = e.deltaY > 0;
        const zoomFactor = 1.1; // Шаг зума 10%

        setScale((prev) => {
          let newScale = zoomOut ? prev / zoomFactor : prev * zoomFactor;
          return Math.min(Math.max(newScale, 0.1), 15); // Лимиты от 0.1x до 15x
        });
      }
    };

    // ВАЖНО: вешаем обработчик глобально на window с { passive: false }
    // Это на 100% блокирует браузерный зум колесиком, где бы ни находился курсор мыши
    window.addEventListener("wheel", handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, [visible]);

  // ФУНКЦИИ КНОПОК ЗУМА
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev * 1.1, 15));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev / 1.1, 0.1));
  };

  const openSettingsModal = () => {
    setSettingsMode("full");
    setSettingsModalVisible(true);
  };

  const handleRequestOpenSettings = () => {
    setSettingsMode("filter");
    setSettingsModalVisible(true);
  };

  const annotationsAbortController = useRef(null);
  const additionalAnnotationsAbortController = useRef(null);

  const rotateImageOnServer = async (imagePath, angle) => {
    try {
      setRotateLoading(true);
      setRotateError(null);

      await axios.post(
        "/api/neural_network_module/rotate-image/",
        {
          image_path: imagePath,
          angle: angle,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 30000,
        },
      );

      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Ошибка при повороте изображения";
      setRotateError(errorMessage);
      return false;
    } finally {
      setRotateLoading(false);
    }
  };

  const rotateImage = async (angle) => {
    if (selectedPhoto && selectedPhoto.fullPath) {
      try {
        setFullImageLoading(true);
        const success = await rotateImageOnServer(selectedPhoto.fullPath, angle);

        if (success) {
          setRotation(0);
          setScale(1); // Сбрасываем масштаб
          if (fullImageSrc) {
            URL.revokeObjectURL(fullImageSrc);
            setFullImageSrc(null);
          }
          await new Promise((resolve) => setTimeout(resolve, 300));
          await loadFullImage(selectedPhoto);

          if (selectedFirstLevelClass) {
            loadAnnotations(
              selectedPhoto,
              selectedFirstLevelClass,
              selectedSecondLevelClass || null,
            );
          }
        }
      } catch (error) {
        console.error("Ошибка при повороте:", error);
        toast.error("Не удалось повернуть изображение");
      } finally {
        setFullImageLoading(false);
      }
    } else {
      setRotation((rotation + angle) % 360);
    }
  };

  const rotateLeft = () => rotateImage(-90);
  const rotateRight = () => rotateImage(90);

  const refreshImage = async () => {
    if (selectedPhoto) {
      setFullImageLoading(true);
      if (fullImageSrc) {
        URL.revokeObjectURL(fullImageSrc);
      }
      await loadFullImage(selectedPhoto);
      setFullImageLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSecondLevelClass && allSecondLevelClasses.length > 0) {
      const foundClass = allSecondLevelClasses.find(
        (cls) => cls.ClassID === selectedSecondLevelClass,
      );
      if (foundClass) {
        const extractedName = extractDefectName(
          foundClass.ClassName || foundClass.Cname || "",
        );
        setCurrentDefectName(extractedName);
        return;
      }
    }
    setCurrentDefectName("Неизвестный дефект");
  }, [selectedSecondLevelClass, allSecondLevelClasses]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if ((e.key === "Delete" || e.key === "Del") && selectedBox) {
        e.preventDefault();
        e.stopPropagation();
        deleteSelectedBox();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, selectedBox]);

  useEffect(() => {
    setRotateError(null);
  }, [selectedPhoto]);

  useEffect(() => {
    if (visible) {
      axios
        .get("/api/auth/permissions/", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.permissions?.is_admin) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        })
        .catch((err) => {
          console.error("Ошибка проверки прав:", err);
          setIsAdmin(false);
        });
    }
  }, [visible, token]);

  useEffect(() => {
    if (!visible) return;
    const fetchAllFirstLevelClasses = async () => {
      try {
        const response = await axios.get(
          `/api/neural_network_module/firstlevelclasses/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAllFirstLevelClasses(response.data || []);
      } catch (error) {
        console.error("Ошибка загрузки классов первого уровня:", error);
      }
    };
    fetchAllFirstLevelClasses();
  }, [visible, token]);

  useEffect(() => {
    if (!visible) return;
    const fetchAllSecondLevelClasses = async () => {
      try {
        const response = await axios.get(
          `/api/neural_network_module/secondlevelclasses/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAllSecondLevelClasses(response.data || []);
      } catch (error) {
        console.error("Ошибка загрузки классов второго уровня:", error);
      }
    };
    fetchAllSecondLevelClasses();
  }, [visible, token]);

  const fetchSecondLevel = async (firstClassId) => {
    try {
      const response = await axios.get(
        `/api/neural_network_module/${objectData.Id}/tree-second-level/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { first_class_id: firstClassId },
        },
      );
      return response.data || [];
    } catch (error) {
      console.error("Ошибка загрузки второго уровня:", error);
      return [];
    }
  };

  const fetchImagesByFirstLevel = async (firstClassId) => {
    try {
      const response = await axios.get(
        `/api/neural_network_module/${objectData.Id}/tree-images/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { first_class_id: firstClassId },
        },
      );
      return response.data || [];
    } catch (error) {
      console.error("Ошибка загрузки изображений первого уровня:", error);
      return [];
    }
  };

  const fetchImagesBySecondLevel = async (firstClassId, secondClassId) => {
    try {
      const response = await axios.get(
        `/api/neural_network_module/${objectData.Id}/tree-images/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            first_class_id: firstClassId,
            second_class_id: secondClassId,
          },
        },
      );
      return response.data || [];
    } catch (error) {
      console.error("Ошибка загрузки изображений второго уровня:", error);
      return [];
    }
  };

  useEffect(() => {
    if (!visible || !objectData?.Id) return;

    const reloadTreeData = async () => {
      setTreeLoading(true);

      try {
        const response = await axios.get(
          `/api/neural_network_module/${objectData.Id}/tree-first-level/`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setFirstLevelItems(response.data || []);

        const currentExpanded = expandedItemsRef.current;
        if (currentExpanded.size > 0) {
          const newSecondLevelItems = { ...secondLevelItems };
          const promises = Array.from(currentExpanded).map(async (classId) => {
            try {
              const data = await fetchSecondLevel(classId);
              newSecondLevelItems[classId] = data;
            } catch (e) {
              console.error(e);
            }
          });
          await Promise.all(promises);
          setSecondLevelItems(newSecondLevelItems);
        }
      } catch (error) {
        console.error("Ошибка обновления дерева:", error);
      } finally {
        setTreeLoading(false);
      }
    };

    reloadTreeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, objectData?.Id, token, refreshTrigger]);

  useEffect(() => {
    if (!visible || !objectData?.Id || !selectedFirstLevelClass) return;

    const reloadThumbnails = async () => {
      setLoading(true);

      try {
        let images = [];
        if (selectedSecondLevelClass) {
          images = await fetchImagesBySecondLevel(
            selectedFirstLevelClass,
            selectedSecondLevelClass,
          );
        } else {
          images = await fetchImagesByFirstLevel(selectedFirstLevelClass);
        }
        await loadThumbnailsForImages(images);
      } catch (error) {
        console.error("Ошибка при обновлении списка изображений:", error);
        setLoading(false);
      }
    };

    reloadThumbnails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    objectData?.Id,
    token,
    selectedFirstLevelClass,
    selectedSecondLevelClass,
    refreshTrigger,
  ]);

  const getFileNameFromPath = (filePath) => {
    return filePath.split("\\").pop().split("/").pop();
  };

  const getFullSizePath = (thumbnailPath) => {
    return thumbnailPath.replace("/Фото/Thumbnails/", "/Фото/");
  };

  const loadThumbnailsForImages = async (imageList) => {
    if (!objectData?.LDoc || imageList.length === 0) {
      setPhotos([]);
      return [];
    }
    setLoading(true);
    const THUMBNAILS_ROOT = "/Фото/Thumbnails";
    const imageExtensions = ["jpg", "jpeg", "png", "webp", "gif"];

    const getMimeType = (filename) => {
      const ext = filename?.split(".").pop()?.toLowerCase();
      switch (ext) {
        case "png":
          return "image/png";
        case "gif":
          return "image/gif";
        case "webp":
          return "image/webp";
        default:
          return "image/jpeg";
      }
    };

    try {
      const response = await axios.get(
        `/api/ldoc/${objectData.LDoc}/workdir/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const files = response.data || [];
      if (!Array.isArray(files)) return [];

      const thumbnailMap = {};
      files.forEach((file) => {
        if (!file.isDirectory) {
          const ext = file.name?.split(".").pop()?.toLowerCase();
          if (
            ext &&
            imageExtensions.includes(ext) &&
            file.path.startsWith(THUMBNAILS_ROOT)
          ) {
            const fileName = getFileNameFromPath(file.path);
            thumbnailMap[fileName] = file;
          }
        }
      });

      const photoRequests = imageList.map(async (imageItem) => {
        const fileName = getFileNameFromPath(imageItem.FilePath);
        const thumbnailFile = thumbnailMap[fileName];
        if (!thumbnailFile) return null;

        try {
          const previewUrl = `/api/ldoc/${objectData.LDoc}/workdir/preview/?path=${encodeURIComponent(
            thumbnailFile.path,
          )}`;
          const res = await axios.get(previewUrl, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });

          if (!res.data || res.data.size === 0) return null;

          const mimeType = getMimeType(thumbnailFile.name);
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);

          return {
            path: thumbnailFile.path,
            url,
            name: thumbnailFile.name,
            fullPath: imageItem.FilePath,
          };
        } catch (err) {
          return null;
        }
      });

      const results = await Promise.all(photoRequests);
      const validPhotos = results.filter((photo) => photo !== null);

      setPhotos(validPhotos);

      if (validPhotos.length > 0) {
        if (selectedPhoto && validPhotos.find((p) => p.path === selectedPhoto.path)) {
          // Текущее фото все еще в списке
        } else {
          handleThumbnailClick(validPhotos[0]);
        }
      } else {
        setSelectedPhoto(null);
        setFullImageSrc(null);
        setAnnotations([]);
        setAdditionalAnnotations([]);
        setShowAnnotations(true);
      }
      return validPhotos;
    } catch (err) {
      console.error("Ошибка загрузки миниатюр:", err);
      setPhotos([]);
      setAnnotations([]);
      setAdditionalAnnotations([]);
      setShowAnnotations(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImageLoad = () => {
    if (imageRef.current) {
      const displayWidth = imageRef.current.clientWidth;
      const displayHeight = imageRef.current.clientHeight;
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      setImageDisplaySize({ width: displayWidth, height: displayHeight });
      setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
    }
  };

  const loadFullImage = (photo) => {
    const fullSizePath = getFullSizePath(photo.path);
    const fullImageUrl = `/api/ldoc/${objectData.LDoc}/workdir/preview/?path=${encodeURIComponent(
      fullSizePath,
    )}`;

    setFullImageLoading(true);
    setFullImageSrc(null);
    setImageDisplaySize({ width: 0, height: 0 });
    setImageNaturalSize({ width: 0, height: 0 });

    if (fullImageSrc) URL.revokeObjectURL(fullImageSrc);

    axios
      .get(fullImageUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      })
      .then((res) => {
        if (!res.data || res.data.size === 0) throw new Error("Empty response");
        const blob = new Blob([res.data], {
          type: res.data.type || "image/jpeg",
        });
        const url = URL.createObjectURL(blob);
        setFullImageSrc(url);
      })
      .catch((err) => {
        console.error("❌ Ошибка загрузки полного изображения:", err);
        setFullImageSrc(null);
      })
      .finally(() => {
        setFullImageLoading(false);
      });
  };

  const loadAnnotations = async (photo, firstClassId, secondClassId = null) => {
    if (annotationsAbortController.current) {
      annotationsAbortController.current.abort();
    }
    const controller = new AbortController();
    annotationsAbortController.current = controller;

    if (!photo || !firstClassId || !objectData?.Id) {
      setAnnotations([]);
      setShowAnnotations(true);
      return;
    }

    setAnnotationsLoading(true);
    setAnnotations([]);

    try {
      const apiUrl = `/api/neural_network_module/${objectData.Id}/tree-annotations/`;
      const params = {
        first_class_id: firstClassId,
        image_path: photo.fullPath,
      };

      if (secondClassId) {
        params.slc_class_id = secondClassId;
      }

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
        signal: controller.signal,
      });

      let processedAnnotations = [];
      (response.data || []).forEach((ann) => {
        const id = `api-${ann.ID || Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        if (
          typeof ann.x1 === "number" &&
          typeof ann.y1 === "number" &&
          typeof ann.x2 === "number" &&
          typeof ann.y2 === "number"
        ) {
          processedAnnotations.push({
            ...ann,
            id,
            x: ann.x1,
            y: ann.y1,
            w: ann.x2 - ann.x1,
            h: ann.y2 - ann.y1,
            isApiAnnotation: true,
          });
        } else if (ann.flaPath && typeof ann.flaPath === "string") {
          const bboxStrings = ann.flaPath.split(";");
          bboxStrings.forEach((bboxStr) => {
            const coords = bboxStr
              .split(",")
              .map((str) => parseFloat(str.trim()));
            if (coords.length === 4 && coords.every((c) => !isNaN(c))) {
              processedAnnotations.push({
                ...ann,
                id,
                x: coords[0],
                y: coords[1],
                w: coords[2] - coords[0],
                h: coords[3] - coords[1],
                isApiAnnotation: true,
              });
            }
          });
        }
      });

      setAnnotations(processedAnnotations);
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error("Ошибка загрузки аннотаций:", error);
      setAnnotations([]);
    } finally {
      if (annotationsAbortController.current === controller) {
        setAnnotationsLoading(false);
      }
    }
  };

  const loadAdditionalAnnotations = async () => {
    if (additionalAnnotationsAbortController.current) {
      additionalAnnotationsAbortController.current.abort();
    }
    const controller = new AbortController();
    additionalAnnotationsAbortController.current = controller;

    if (!selectedPhoto || !objectData?.Id) {
      return;
    }

    setAnnotationsLoading(true);
    setAdditionalAnnotations([]);

    try {
      const apiUrl = `/api/neural_network_module/${objectData.Id}/tree-annotations/`;
      const params = {
        image_path: selectedPhoto.fullPath,
      };

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
        signal: controller.signal,
      });

      let processedAnnotations = [];
      (response.data || []).forEach((ann) => {
        const id = `api-${ann.ID || Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        if (
          typeof ann.x1 === "number" &&
          typeof ann.y1 === "number" &&
          typeof ann.x2 === "number" &&
          typeof ann.y2 === "number"
        ) {
          processedAnnotations.push({
            ...ann,
            id,
            x: ann.x1,
            y: ann.y1,
            w: ann.x2 - ann.x1,
            h: ann.y2 - ann.y1,
            isApiAnnotation: true,
          });
        } else if (ann.flaPath && typeof ann.flaPath === "string") {
          const bboxStrings = ann.flaPath.split(";");
          bboxStrings.forEach((bboxStr) => {
            const coords = bboxStr
              .split(",")
              .map((str) => parseFloat(str.trim()));
            if (coords.length === 4 && coords.every((c) => !isNaN(c))) {
              processedAnnotations.push({
                ...ann,
                id,
                x: coords[0],
                y: coords[1],
                w: coords[2] - coords[0],
                h: coords[3] - coords[1],
                isApiAnnotation: true,
              });
            }
          });
        }
      });

      setAdditionalAnnotations(processedAnnotations);
      setShowAdditionalAnnotations(true);
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error("Ошибка загрузки дополнительных аннотаций:", error);
      setAdditionalAnnotations([]);
    } finally {
      if (additionalAnnotationsAbortController.current === controller) {
        setAnnotationsLoading(false);
      }
    }
  };

  const handleThumbnailClick = (photo) => {
    setSelectedPhoto(photo);
    setRotation(0);
    setScale(1); // Сбрасываем масштаб при переходе
    setRotateError(null);
    loadFullImage(photo);

    setAdditionalAnnotations([]);
    setShowAdditionalAnnotations(true);
    setIsDrawingMode(false);
    setSavedBoxes([]);
    setSelectedBox(null);
    setCoordinatesForPropertiesPanel(null);
    setApiDataForPropertiesPanel(null);
    setTempBox(null);
    setIsDrawing(false);
    setDraggingBox(null);
    setDragOffset({ x: 0, y: 0 });
    setResizingBox(null);
    setResizeHandle(null);
  };

  const toggleItem = async (firstClassId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(firstClassId)) {
      newExpanded.delete(firstClassId);
    } else {
      newExpanded.add(firstClassId);
      if (!secondLevelItems[firstClassId]) {
        const data = await fetchSecondLevel(firstClassId);
        setSecondLevelItems((prev) => ({ ...prev, [firstClassId]: data }));
      }
    }
    setExpandedItems(newExpanded);
  };

  const handleFirstLevelClick = async (firstClassItem) => {
    try {
      setSelectedFirstLevelClass(firstClassItem.ClassID);
      setSelectedSecondLevelClass("");

      setSelectedBox(null);
      setCoordinatesForPropertiesPanel(null);
      setApiDataForPropertiesPanel(null);

      const secondLevelData = await fetchSecondLevel(firstClassItem.ClassID);
      setSecondLevelItems((prev) => ({
        ...prev,
        [firstClassItem.ClassID]: secondLevelData,
      }));
      const images = await fetchImagesByFirstLevel(firstClassItem.ClassID);
      await loadThumbnailsForImages(images);
    } catch (error) {
      console.error("Ошибка при клике на первый уровень:", error);
    }
  };

  const handleSecondLevelClick = async (firstClassId, secondClassItem) => {
    try {
      setSelectedFirstLevelClass(firstClassId);
      setSelectedSecondLevelClass(secondClassItem.ClassID);

      setSelectedBox(null);
      setCoordinatesForPropertiesPanel(null);
      setApiDataForPropertiesPanel(null);

      const images = await fetchImagesBySecondLevel(
        firstClassId,
        secondClassItem.ClassID,
      );
      await loadThumbnailsForImages(images);
      setAdditionalAnnotations([]);
      setShowAdditionalAnnotations(true);
    } catch (error) {
      console.error("Ошибка при клике на второй уровень:", error);
    }
  };

  useEffect(() => {
    if (!selectedBox && !fullImageLoading && !annotationsLoading) {
      if (annotations.length > 0) {
        const firstBox = annotations[0];
        const boxData = {
          ...firstBox,
          id: `annotation-${firstBox.id}`,
          isApiAnnotation: true,
        };
        setSelectedBox(boxData);
        setCoordinatesForPropertiesPanel({
          x: firstBox.x,
          y: firstBox.y,
          w: firstBox.w,
          h: firstBox.h,
        });
        setApiDataForPropertiesPanel(firstBox);
      } else if (additionalAnnotations.length > 0) {
        const firstBox = additionalAnnotations[0];
        const boxData = {
          ...firstBox,
          id: `additional-${firstBox.id}`,
          isApiAnnotation: true,
        };
        setSelectedBox(boxData);
        setCoordinatesForPropertiesPanel({
          x: firstBox.x,
          y: firstBox.y,
          w: firstBox.w,
          h: firstBox.h,
        });
        setApiDataForPropertiesPanel(firstBox);
      }
    }
  }, [
    annotations,
    additionalAnnotations,
    fullImageLoading,
    annotationsLoading,
    selectedBox,
  ]);

  useEffect(() => {
    if (selectedPhoto && selectedFirstLevelClass) {
      loadAnnotations(
        selectedPhoto,
        selectedFirstLevelClass,
        selectedSecondLevelClass || null,
      );
    }
  }, [selectedFirstLevelClass, selectedPhoto]);

  useEffect(() => {
    return () => {
      if (fullImageSrc) URL.revokeObjectURL(fullImageSrc);
    };
  }, [fullImageSrc]);

  const handleAddImage = () => setAddDefectsModalVisible(true);
  const handleViewReport = () => setShowReportModal(true);
  const handleDrawBox = () => {
    if (!selectedFirstLevelClass || !selectedSecondLevelClass) {
      alert(
        "Пожалуйста, выберите класс из дерева слева или из панели свойств",
      );
      return;
    }
    setIsDrawingMode((prev) => !prev);
    if (!isDrawingMode) {
      setSelectedBox(null);
      setCoordinatesForPropertiesPanel(null);
      setApiDataForPropertiesPanel(null);
      setDraggingBox(null);
      setDragOffset({ x: 0, y: 0 });
      setResizingBox(null);
      setResizeHandle(null);
    }
  };

  const handleProperties = () => setShowBottomPanel((prev) => !prev);

  const handleShowDefects = () => {
    if (additionalAnnotations.length > 0) {
      setShowAdditionalAnnotations((prev) => !prev);
    } else {
      loadAdditionalAnnotations().catch(() => {});
    }
  };

  const closeSettingsModal = () => setSettingsModalVisible(false);

  const getRawId = (id) => {
    if (typeof id === "string") {
      if (id.startsWith("saved-")) return Number(id.replace("saved-", ""));
      if (id.startsWith("annotation-")) return id.replace("annotation-", "");
      if (id.startsWith("additional-")) return id.replace("additional-", "");
    }
    return id;
  };

  const findClickedBox = (boxes, x, y, prefix) => {
    return boxes.find((box) => {
      const boxX = Math.min(box.x, box.x + box.w);
      const boxY = Math.min(box.y, box.y + box.h);
      const boxW = Math.abs(box.w);
      const boxH = Math.abs(box.h);
      const dx = x - boxX;
      const dy = y - boxY;
      return dx >= 0 && dx <= boxW && dy >= 0 && dy <= boxH;
    });
  };

  const getResizeHandle = (box, x, y, img, currentScale) => {
    if (!img) return null;
    const actualOffsetWidth = img.offsetWidth * currentScale;
    const actualOffsetHeight = img.offsetHeight * currentScale;
    const scaleX = actualOffsetWidth / img.naturalWidth;
    const scaleY = actualOffsetHeight / img.naturalHeight;

    const boxLeft = box.x * scaleX;
    const boxTop = box.y * scaleY;
    const boxRight = (box.x + box.w) * scaleX;
    const boxBottom = (box.y + box.h) * scaleY;

    const handleSize = 8 * currentScale;
    const tolerance = 4 * currentScale;

    if (
      Math.abs(x - boxLeft) <= handleSize + tolerance &&
      Math.abs(y - boxTop) <= handleSize + tolerance
    )
      return "nw";
    if (
      Math.abs(x - boxRight) <= handleSize + tolerance &&
      Math.abs(y - boxTop) <= handleSize + tolerance
    )
      return "ne";
    if (
      Math.abs(x - boxLeft) <= handleSize + tolerance &&
      Math.abs(y - boxBottom) <= handleSize + tolerance
    )
      return "sw";
    if (
      Math.abs(x - boxRight) <= handleSize + tolerance &&
      Math.abs(y - boxBottom) <= handleSize + tolerance
    )
      return "se";
    if (
      Math.abs(x - (boxLeft + boxRight) / 2) <= handleSize + tolerance &&
      Math.abs(y - boxTop) <= handleSize + tolerance
    )
      return "n";
    if (
      Math.abs(x - (boxLeft + boxRight) / 2) <= handleSize + tolerance &&
      Math.abs(y - boxBottom) <= handleSize + tolerance
    )
      return "s";
    if (
      Math.abs(x - boxLeft) <= handleSize + tolerance &&
      Math.abs(y - (boxTop + boxBottom) / 2) <= handleSize + tolerance
    )
      return "w";
    if (
      Math.abs(x - boxRight) <= handleSize + tolerance &&
      Math.abs(y - (boxTop + boxBottom) / 2) <= handleSize + tolerance
    )
      return "e";
    return null;
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const img = imageRef.current;
    
    // Пересчет с учетом zoom CSS transform (scale)
    const actualOffsetWidth = img.offsetWidth * scale;
    const actualOffsetHeight = img.offsetHeight * scale;
    const scaleX = img.naturalWidth / actualOffsetWidth;
    const scaleY = img.naturalHeight / actualOffsetHeight;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    if (selectedBox) {
      const handle = getResizeHandle(
        selectedBox,
        e.clientX - rect.left,
        e.clientY - rect.top,
        img,
        scale
      );
      if (handle) {
        setResizingBox(selectedBox);
        setResizeHandle(handle);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    const clickedSavedBox = findClickedBox(savedBoxes, x, y, "saved");
    if (clickedSavedBox) {
      const selectedBoxData = {
        ...clickedSavedBox,
        id: `saved-${clickedSavedBox.id}`,
        isSavedBox: true,
      };
      setSelectedBox(selectedBoxData);
      setCoordinatesForPropertiesPanel({
        x: clickedSavedBox.x,
        y: clickedSavedBox.y,
        w: clickedSavedBox.w,
        h: clickedSavedBox.h,
      });
      setApiDataForPropertiesPanel(null);
      setDraggingBox(clickedSavedBox);
      setDragOffset({ x: x - clickedSavedBox.x, y: y - clickedSavedBox.y });
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const clickedAnnotation = findClickedBox(annotations, x, y, "annotation");
    if (clickedAnnotation) {
      const selectedBoxData = {
        ...clickedAnnotation,
        id: `annotation-${clickedAnnotation.id}`,
        isApiAnnotation: true,
      };
      setSelectedBox(selectedBoxData);
      setCoordinatesForPropertiesPanel({
        x: clickedAnnotation.x,
        y: clickedAnnotation.y,
        w: clickedAnnotation.w,
        h: clickedAnnotation.h,
      });
      setApiDataForPropertiesPanel(clickedAnnotation);
      setDraggingBox(clickedAnnotation);
      setDragOffset({ x: x - clickedAnnotation.x, y: y - clickedAnnotation.y });
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const clickedAdditionalAnnotation = findClickedBox(
      additionalAnnotations,
      x,
      y,
      "additional",
    );
    if (clickedAdditionalAnnotation) {
      const selectedBoxData = {
        ...clickedAdditionalAnnotation,
        id: `additional-${clickedAdditionalAnnotation.id}`,
        isApiAnnotation: true,
      };
      setSelectedBox(selectedBoxData);
      setCoordinatesForPropertiesPanel({
        x: clickedAdditionalAnnotation.x,
        y: clickedAdditionalAnnotation.y,
        w: clickedAdditionalAnnotation.w,
        h: clickedAdditionalAnnotation.h,
      });
      setApiDataForPropertiesPanel(clickedAdditionalAnnotation);
      setDraggingBox(clickedAdditionalAnnotation);
      setDragOffset({
        x: x - clickedAdditionalAnnotation.x,
        y: y - clickedAdditionalAnnotation.y,
      });
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isDrawingMode) {
      e.stopPropagation();
      const startPos = { x, y };
      setDrawStartPos(startPos);
      setTempBox({ x, y, w: 0, h: 0 });
      setIsDrawing(true);

      setSelectedBox(null);
      setDraggingBox(null);
      setResizingBox(null);
      setResizeHandle(null);
      setCoordinatesForPropertiesPanel(null);
      setApiDataForPropertiesPanel(null);
      return;
    }

    setSelectedBox(null);
    setCoordinatesForPropertiesPanel(null);
    setApiDataForPropertiesPanel(null);
    setDraggingBox(null);
    setDragOffset({ x: 0, y: 0 });
    setResizingBox(null);
    setResizeHandle(null);
  };

  const handleMouseMove = (e) => {
    if (isDrawing && tempBox) {
      const rect = imageRef.current.getBoundingClientRect();
      const img = imageRef.current;
      const actualOffsetWidth = img.offsetWidth * scale;
      const actualOffsetHeight = img.offsetHeight * scale;
      const scaleX = img.naturalWidth / actualOffsetWidth;
      const scaleY = img.naturalHeight / actualOffsetHeight;
      const currentX = (e.clientX - rect.left) * scaleX;
      const currentY = (e.clientY - rect.top) * scaleY;
      const startX = tempBox.x;
      const startY = tempBox.y;
      const minX = Math.min(startX, currentX);
      const minY = Math.min(startY, currentY);
      const maxX = Math.max(startX, currentX);
      const maxY = Math.max(startY, currentY);
      setTempBox({ x: minX, y: minY, w: maxX - minX, h: maxY - minY });
    }

    if (draggingBox && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const img = imageRef.current;
      const actualOffsetWidth = img.offsetWidth * scale;
      const actualOffsetHeight = img.offsetHeight * scale;
      const scaleX = img.naturalWidth / actualOffsetWidth;
      const scaleY = img.naturalHeight / actualOffsetHeight;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      let updatedBoxData = null;

      const updateList = (list, setList) => {
        const updated = list.map((box) =>
          box.id === draggingBox.id
            ? { ...box, x: x - dragOffset.x, y: y - dragOffset.y }
            : box,
        );
        setList(updated);
        return updated.find((b) => b.id === draggingBox.id);
      };

      if (savedBoxes.some((box) => box.id === draggingBox.id))
        updatedBoxData = updateList(savedBoxes, setSavedBoxes);
      else if (annotations.some((box) => box.id === draggingBox.id))
        updatedBoxData = updateList(annotations, setAnnotations);
      else if (additionalAnnotations.some((box) => box.id === draggingBox.id))
        updatedBoxData = updateList(
          additionalAnnotations,
          setAdditionalAnnotations,
        );

      if (selectedBox && updatedBoxData) {
        setCoordinatesForPropertiesPanel({
          x: updatedBoxData.x,
          y: updatedBoxData.y,
          w: updatedBoxData.w,
          h: updatedBoxData.h,
        });

        setSelectedBox((prev) =>
          prev ? { ...prev, ...updatedBoxData, id: prev.id } : null,
        );
      }
    }
    if (isDrawing && drawStartPos && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const img = imageRef.current;
      const actualOffsetWidth = img.offsetWidth * scale;
      const actualOffsetHeight = img.offsetHeight * scale;
      const scaleX = img.naturalWidth / actualOffsetWidth;
      const scaleY = img.naturalHeight / actualOffsetHeight;

      const currentX = (e.clientX - rect.left) * scaleX;
      const currentY = (e.clientY - rect.top) * scaleY;

      const newX = Math.min(drawStartPos.x, currentX);
      const newY = Math.min(drawStartPos.y, currentY);
      const newW = Math.abs(currentX - drawStartPos.x);
      const newH = Math.abs(currentY - drawStartPos.y);

      setTempBox({ x: newX, y: newY, w: newW, h: newH });
    }

    if (resizingBox && resizeHandle && imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const img = imageRef.current;
      const actualOffsetWidth = img.offsetWidth * scale;
      const actualOffsetHeight = img.offsetHeight * scale;
      const scaleX = img.naturalWidth / actualOffsetWidth;
      const scaleY = img.naturalHeight / actualOffsetHeight;
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;

      let { x: newX, y: newY, w: newW, h: newH } = resizingBox;
      const { x: boxX, y: boxY, w: boxW, h: boxH } = resizingBox;

      switch (resizeHandle) {
        case "e":
          newW = Math.max(10, mouseX - boxX);
          break;
        case "w":
          newW = Math.max(10, boxX + boxW - mouseX);
          newX = Math.min(boxX + boxW - 10, mouseX);
          break;
        case "n":
          newH = Math.max(10, boxY + boxH - mouseY);
          newY = Math.min(boxY + boxH - 10, mouseY);
          break;
        case "s":
          newH = Math.max(10, mouseY - boxY);
          break;
        case "nw":
          newW = Math.max(10, boxX + boxW - mouseX);
          newH = Math.max(10, boxY + boxH - mouseY);
          newX = Math.min(boxX + boxW - 10, mouseX);
          newY = Math.min(boxY + boxH - 10, mouseY);
          break;
        case "ne":
          newW = Math.max(10, mouseX - boxX);
          newH = Math.max(10, boxY + boxH - mouseY);
          newY = Math.min(boxY + boxH - 10, mouseY);
          break;
        case "sw":
          newW = Math.max(10, boxX + boxW - mouseX);
          newH = Math.max(10, mouseY - boxY);
          newX = Math.min(boxX + boxW - 10, mouseX);
          break;
        case "se":
          newW = Math.max(10, mouseX - boxX);
          newH = Math.max(10, mouseY - boxY);
          break;
      }

      let updatedBoxData = null;
      const updateList = (list, setList) => {
        const updated = list.map((box) =>
          box.id === getRawId(resizingBox.id)
            ? { ...box, x: newX, y: newY, w: newW, h: newH }
            : box,
        );
        setList(updated);
        return updated.find((b) => b.id === getRawId(resizingBox.id));
      };

      if (savedBoxes.some((box) => box.id === getRawId(resizingBox.id)))
        updatedBoxData = updateList(savedBoxes, setSavedBoxes);
      else if (annotations.some((box) => box.id === getRawId(resizingBox.id)))
        updatedBoxData = updateList(annotations, setAnnotations);
      else if (
        additionalAnnotations.some((box) => box.id === getRawId(resizingBox.id))
      )
        updatedBoxData = updateList(
          additionalAnnotations,
          setAdditionalAnnotations,
        );

      if (selectedBox && updatedBoxData) {
        setSelectedBox({ ...selectedBox, x: newX, y: newY, w: newW, h: newH });
        setCoordinatesForPropertiesPanel({
          x: newX,
          y: newY,
          w: newW,
          h: newH,
        });
      }
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && tempBox) {
      if (Math.abs(tempBox.w) > 5 && Math.abs(tempBox.h) > 5) {
        const newBox = {
          ...tempBox,
          defect: currentDefectName,
          color: markerColor,
          id: Date.now(),
          isSavedBox: true,
        };

        setSavedBoxes((prev) => [...prev, newBox]);

        const selectedBoxData = { ...newBox, id: `saved-${newBox.id}` };
        setSelectedBox(selectedBoxData);
        setCoordinatesForPropertiesPanel({
          x: newBox.x,
          y: newBox.y,
          w: newBox.w,
          h: newBox.h,
        });
        setApiDataForPropertiesPanel(null);

        setIsDrawingMode(false);
      }

      setTempBox(null);
      setIsDrawing(false);
      setDrawStartPos(null);
    }

    if (draggingBox) {
      setDraggingBox(null);
      setDragOffset({ x: 0, y: 0 });
    }
    if (resizingBox) {
      setResizingBox(null);
      setResizeHandle(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDrawing, tempBox, draggingBox, dragOffset, resizingBox, resizeHandle, scale]);

  const deleteSelectedBox = async () => {
    if (!selectedBox) {
      console.log("Нет выбранной рамки для удаления");
      return;
    }

    if (!window.confirm("Вы уверены, что хотите удалить выбранную рамку?")) {
      return;
    }

    try {
      if (selectedBox.isSavedBox) {
        const numericId = Number(selectedBox.id.replace("saved-", ""));
        setSavedBoxes((prev) => prev.filter((box) => box.id !== numericId));
      }
      if (selectedBox.slaId) {
        await axios.delete(`/api/neural_network_module/second-level-ai/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { id: selectedBox.slaId },
        });
      } else if (selectedBox.isApiAnnotation) {
        const recordId = selectedBox.flaId || selectedBox.ID;
        if (selectedBox.flaId) {
          const recordId = selectedBox.flaId;
          await axios.delete(`/api/neural_network_module/first-level-ai/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { id: recordId },
          });
        }

        const originalApiId = selectedBox.id
          .replace("annotation-", "")
          .replace("additional-", "");
        setAnnotations((prev) =>
          prev.filter((box) => box.id !== originalApiId),
        );
        setAdditionalAnnotations((prev) =>
          prev.filter((box) => box.id !== originalApiId),
        );
      }

      setRefreshTrigger((prev) => prev + 1);

      if (selectedPhoto && selectedFirstLevelClass) {
        loadAnnotations(
          selectedPhoto,
          selectedFirstLevelClass,
          selectedSecondLevelClass || null,
        );
      }

      setCoordinatesForPropertiesPanel(null);
      setApiDataForPropertiesPanel(null);
      setSelectedBox(null);
    } catch (error) {
      console.error("Ошибка при удалении рамки:", error);
      alert("Ошибка при удалении рамки: " + error.message);
    }
  };

  const deleteSelectedPhoto = async () => {
    if (!selectedPhoto || !objectData?.Id) {
      alert("Пожалуйста, выберите фото для удаления.");
      return;
    }
    if (
      !window.confirm(
        `Вы уверены, что хотите удалить все аннотации для фото "${selectedPhoto.name}"?`,
      )
    )
      return;

    try {
      const response = await axios.get(
        `/api/neural_network_module/${objectData.Id}/tree-annotations/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { image_path: selectedPhoto.fullPath },
        },
      );

      const annotationsToDelete = response.data || [];
      await Promise.all(
        annotationsToDelete
          .filter((ann) => ann.flaId)
          .map((ann) =>
            axios.delete(`/api/neural_network_module/first-level-ai/`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { id: ann.flaId },
            }),
          ),
      );

      setAnnotations([]);
      setAdditionalAnnotations([]);

      const updatedPhotos = photos.filter(
        (photo) => photo.path !== selectedPhoto.path,
      );
      setPhotos(updatedPhotos);

      if (updatedPhotos.length === 0 && selectedFirstLevelClass) {
        setFirstLevelItems((prev) =>
          prev.filter((item) => item.ClassID !== selectedFirstLevelClass),
        );

        setSecondLevelItems((prev) => {
          const newState = { ...prev };
          delete newState[selectedFirstLevelClass];
          return newState;
        });

        setSelectedFirstLevelClass("");
        setSelectedSecondLevelClass("");
      }

      setSelectedPhoto(null);
      setFullImageSrc(null);
      setRefreshTrigger((prev) => prev + 1);

      alert("Все аннотации для выбранного фото успешно удалены.");
    } catch (error) {
      console.error("Ошибка удаления:", error);
      alert(
        "Ошибка при удалении: " +
          (error.response?.data?.message || error.message),
      );
    }
  };

  const handleAddDefectsModalClose = () => {
    setAddDefectsModalVisible(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const Spinner = () => (
    <div
      style={{
        width: "32px",
        height: "32px",
        border: "3px solid #ddd",
        borderTop: "3px solid #0d6efd",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        margin: "20px auto",
      }}
    ></div>
  );

  const getBoxStyle = (box) => {
    const img = imageRef.current;
    if (!img) return {};
    const scaleX = img.offsetWidth / img.naturalWidth;
    const scaleY = img.offsetHeight / img.naturalHeight;
    const x = box.x * scaleX;
    const y = box.y * scaleY;
    const w = Math.abs(box.w * scaleX);
    const h = Math.abs(box.h * scaleY);
    const borderColor = box.slaColor || box.color || markerColor || "#ff0000";

    return {
      position: "absolute",
      left: `${x}px`,
      top: `${y}px`,
      width: `${w}px`,
      height: `${h}px`,
      border: `2px solid ${borderColor}`,
      backgroundColor: "transparent",
      pointerEvents: "auto",
      zIndex: 10,
      cursor: "move",
    };
  };

  const ResizeHandles = ({ box }) => {
    if (!imageRef.current) return null;
    const img = imageRef.current;
    const scaleX = img.offsetWidth / img.naturalWidth;
    const scaleY = img.offsetHeight / img.naturalHeight;
    const x = box.x * scaleX;
    const y = box.y * scaleY;
    const w = Math.abs(box.w * scaleX);
    const h = Math.abs(box.h * scaleY);
    const handleSize = 8;
    const handleStyle = {
      position: "absolute",
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      backgroundColor: "#007bff",
      border: "1px solid white",
      borderRadius: "2px",
      zIndex: 20,
      cursor: "move",
      transform: "translate(-50%, -50%)",
      pointerEvents: "auto",
    };

    return (
      <>
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x}px`,
            top: `${y}px`,
            cursor: "nw-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("nw");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x + w}px`,
            top: `${y}px`,
            cursor: "ne-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("ne");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x}px`,
            top: `${y + h}px`,
            cursor: "sw-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("sw");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x + w}px`,
            top: `${y + h}px`,
            cursor: "se-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("se");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x + w / 2}px`,
            top: `${y}px`,
            cursor: "n-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("n");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x + w / 2}px`,
            top: `${y + h}px`,
            cursor: "s-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("s");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x}px`,
            top: `${y + h / 2}px`,
            cursor: "w-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("w");
          }}
        />
        <div
          className="resize-handle"
          style={{
            ...handleStyle,
            left: `${x + w}px`,
            top: `${y + h / 2}px`,
            cursor: "e-resize",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setResizingBox(box);
            setResizeHandle("e");
          }}
        />
      </>
    );
  };

  const renderAnnotations = (
    annotationsList,
    color = "#ff0000",
    labelPrefix = "",
  ) => {
    if (!imageDisplaySize.width || !imageNaturalSize.width || !showAnnotations)
      return null;
    if (annotationsList === additionalAnnotations && !showAdditionalAnnotations)
      return null;
    if (annotationsLoading && annotationsList === additionalAnnotations)
      return null;

    return annotationsList.map((box) => {
      const borderColor = box.slaColor || box.color || markerColor || color;
      let label = box.defect || "Дефект";
      if (!box.defect && (box.ClassName || box.Cname))
        label = extractDefectName(box.ClassName || box.Cname || "");
      const isSelected =
        selectedBox &&
        selectedBox.id === (box.isSavedBox ? `saved-${box.id}` : box.id);
      const boxColor = isSelected ? "#0000FF" : borderColor;
      const textColor = isDarkColor(boxColor) ? "white" : "black";

      return (
        <div
          key={box.id}
          className="box-element"
          style={{
            ...getBoxStyle(box),
            border: `2px solid ${boxColor}`,
            pointerEvents: "auto",
          }}
          title={label}
          onContextMenu={(e) => e.preventDefault()}
        >
          {label && (
            <div
              style={{
                position: "absolute",
                top: "-20px",
                left: "0",
                fontSize: "12px",
                fontWeight: "bold",
                color: textColor,
                whiteSpace: "nowrap",
                background: boxColor,
                padding: "2px 4px",
                borderRadius: "2px",
                pointerEvents: "none",
              }}
            >
              {label}
            </div>
          )}
        </div>
      );
    });
  };

  const filteredAdditionalAnnotations = additionalAnnotations.filter(
    (addAnn) =>
      !annotations.some(
        (ann) =>
          (ann.slaId && addAnn.slaId && ann.slaId === addAnn.slaId) ||
          (Math.abs(ann.x - addAnn.x) < 0.1 &&
            Math.abs(ann.y - addAnn.y) < 0.1 &&
            Math.abs(ann.w - addAnn.w) < 0.1 &&
            Math.abs(ann.h - addAnn.h) < 0.1),
      ),
  );

  return (
    <>
      <CModal
        visible={visible}
        onClose={safeOnClose}
        size="xl"
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            Просмотр дефектов - {objectData?.Name || "Объект"}
            {rotateError && (
              <span
                style={{ color: "red", fontSize: "12px", marginLeft: "10px" }}
              >
                Ошибка поворота: {rotateError}
              </span>
            )}
          </CModalTitle>
        </CModalHeader>

        <div className="main-neural-modal__toolbar">
          <CTooltip content="Добавляет изображение для редактирования">
            <CButton
              className="button main-neural-modal__button"
              color="link"
              size="sm"
              onClick={handleAddImage}
              style={{ textDecoration: "none" }}
            >
              <CIcon icon={cilImagePlus} style={{ marginRight: "5px" }} />{" "}
              Добавить изображение
            </CButton>
          </CTooltip>
          <CButton
            className="button"
            color="link"
            size="sm"
            onClick={handleViewReport}
            style={{ textDecoration: "none" }}
          >
            <CIcon icon={cilDescription} style={{ marginRight: "4px" }} />{" "}
            Просмотр отчета
          </CButton>
          <CTooltip content="Нарисовать рамку выбранного дефекта">
            <CButton
              className="button main-neural-modal__button main-neural-modal__button--painting"
              color={isDrawingMode ? "primary" : "link"}
              size="sm"
              onClick={handleDrawBox}
              style={{ textDecoration: "none" }}
            >
              <CIcon icon={cilBorderOuter} style={{ marginRight: "4px" }} />{" "}
              {isDrawingMode ? "Отменить рисование" : "Нарисовать рамку"}
            </CButton>
          </CTooltip>
          <CTooltip content="Удалить рамку выбранного дефекта">
            <CButton
              className="button main-neural-modal__button main-neural-modal__button--del-box"
              color="link"
              size="sm"
              onClick={deleteSelectedBox}
              style={{ textDecoration: "none" }}
              disabled={!selectedBox}
            >
              <CIcon icon={cilTrash} style={{ marginRight: "4px" }} /> Удалить
              рамку
            </CButton>
          </CTooltip>
          <CTooltip content="Удалить фото">
            <CButton
              className="button main-neural-modal__button main-neural-modal__button--del-photo"
              color="danger"
              size="sm"
              onClick={deleteSelectedPhoto}
              style={{ textDecoration: "none" }}
              disabled={!selectedPhoto}
            >
              <CIcon icon={cilTrash} style={{ marginRight: "4px" }} /> Удалить
              фото
            </CButton>
          </CTooltip>
          <CTooltip content="Показать свойства дефекта">
            <CButton
              className="button main-neural-modal__button"
              color="link"
              size="sm"
              onClick={handleProperties}
              style={{ textDecoration: "none" }}
            >
              <CIcon icon={cilSettings} style={{ marginRight: "4px" }} />{" "}
              Свойства дефекта
            </CButton>
          </CTooltip>
          <CTooltip content="Показать дефекты на фото">
            <CButton
              className="button main-neural-modal__button"
              color="link"
              size="sm"
              onClick={handleShowDefects}
              style={{ textDecoration: "none" }}
              disabled={!selectedPhoto || !objectData?.Id}
            >
              <CIcon icon={cilViewQuilt} style={{ marginRight: "4px" }} />{" "}
              Показать дефекты
            </CButton>
          </CTooltip>
          <CTooltip content="Увеличить (+)">
            <CButton
              className="button main-neural-modal__button-rotate"
              size="sm"
              onClick={handleZoomIn}
              disabled={!selectedPhoto || fullImageLoading}
            >
              <CIcon icon={cilZoomIn} />
            </CButton>
          </CTooltip>
          <CTooltip content="Уменьшить (-)">
            <CButton
              className="button main-neural-modal__button-rotate"
              size="sm"
              onClick={handleZoomOut}
              disabled={!selectedPhoto || fullImageLoading}
            >
              <CIcon icon={cilZoomOut} />
            </CButton>
          </CTooltip>
          <CTooltip content="Повернуть против часовой стрелки (-90°)">
            <CButton
              className="button main-neural-modal__button-rotate main-neural-modal__button-rotate--counterclock-w"
              size="sm"
              onClick={rotateLeft}
              disabled={rotateLoading || fullImageLoading}
              style={{
                position: "relative",
                opacity: rotateLoading || fullImageLoading ? 0.6 : 1,
              }}
            >
              <CIcon icon={cilArrowRight} />
              {(rotateLoading || fullImageLoading) && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "16px",
                    height: "16px",
                    border: "2px solid transparent",
                    borderTop: "2px solid #0d6efd",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
            </CButton>
          </CTooltip>
          <CTooltip content="Повернуть по часовой стрелке (+90°)">
            <CButton
              className="button main-neural-modal__button-rotate"
              size="sm"
              onClick={rotateRight}
              disabled={rotateLoading || fullImageLoading}
              style={{
                position: "relative",
                opacity: rotateLoading || fullImageLoading ? 0.6 : 1,
              }}
            >
              <CIcon icon={cilArrowLeft} style={{ transform: "scaleX(-1)" }} />
              {(rotateLoading || fullImageLoading) && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "16px",
                    height: "16px",
                    border: "2px solid transparent",
                    borderTop: "2px solid #0d6efd",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
            </CButton>
          </CTooltip>
          <CTooltip content="Обновить изображение">
            <CButton
              className="button"
              color="link"
              size="sm"
              onClick={refreshImage}
              style={{ textDecoration: "none" }}
              disabled={!selectedPhoto || fullImageLoading}
            >
              <CIcon icon={cilReload} style={{ marginRight: "4px" }} /> Обновить
            </CButton>
          </CTooltip>
          {isAdmin && (
            <div style={{ marginLeft: "auto" }}>
              <CTooltip content="Настройка параметров нейросети">
                <CButton
                  color="link"
                  size="sm"
                  onClick={openSettingsModal}
                  style={{ textDecoration: "none" }}
                >
                  <CIcon icon={cilSettings} style={{ marginRight: "4px" }} />{" "}
                  Настройка нейросети
                </CButton>
              </CTooltip>
            </div>
          )}
        </div>
        <CModalBody
          style={{
            height: "calc(100vh - 150px)",
            display: "flex",
            padding: 0,
            overflow: "hidden",
            userSelect: "auto",
          }}
        >
          <div
            style={{
              width: `${leftPanelWidth}px`,
              borderRight: "1px solid #dee2e6",
              backgroundColor: "#f8f9fa",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: showBottomPanel ? "50%" : "100%",
                overflowY: "auto",
                borderBottom: showBottomPanel ? "1px solid #dee2e6" : "none",
                transition: "height 0.2s ease",
              }}
            >
              {treeLoading ? (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <Spinner />
                </div>
              ) : firstLevelItems.length === 0 ? (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#6c757d",
                    fontSize: "14px",
                  }}
                >
                  Нет данных
                </div>
              ) : (
                <div style={{ padding: "8px 0" }}>
                  {firstLevelItems.map((item) => (
                    <div key={item.ClassID}>
                      <div
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "background-color 0.2s",
                        }}
                        onClick={() => {
                          toggleItem(item.ClassID);
                          handleFirstLevelClick(item);
                        }}
                      >
                        <CIcon
                          icon={
                            expandedItems.has(item.ClassID)
                              ? cilChevronBottom
                              : cilChevronRight
                          }
                          style={{ marginRight: "8px", fontSize: "12px" }}
                        />
                        <span style={{ fontSize: "14px" }}>{item.CName}</span>
                      </div>
                      {expandedItems.has(item.ClassID) &&
                        secondLevelItems[item.ClassID] && (
                          <div style={{ paddingLeft: "24px" }}>
                            {secondLevelItems[item.ClassID].map(
                              (secondItem) => (
                                <div
                                  key={secondItem.ClassID}
                                  style={{
                                    padding: "6px 12px",
                                    fontSize: "13px",
                                    borderBottom: "1px solid #eee",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSecondLevelClick(
                                      item.ClassID,
                                      secondItem,
                                    );
                                    if (photos.length > 0) {
                                      handleThumbnailClick(photos[0]);
                                    }
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span>{secondItem.Cname}</span>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "12px",
                                        fontSize: "12px",
                                        color: "#6c757d",
                                      }}
                                    >
                                      <span
                                        title={`(${secondItem.WithRec}/${secondItem.Total})`}
                                        style={{ cursor: "help" }}
                                      >
                                        {secondItem.WithRec}/{secondItem.Total}
                                      </span>
                                      <span
                                        title="Коэффициент"
                                        style={{
                                          cursor: "help",
                                          minWidth: "23px",
                                          textAlign: "right",
                                        }}
                                      >
                                        {secondItem.Threshold}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {showBottomPanel && (
              <div style={{ height: "50%" }}>
                <div style={{ height: "100%", overflowY: "auto" }}>
                  <DefectPropertiesPanel
                    selectedFirstLevelClass={selectedFirstLevelClass}
                    selectedSecondLevelClass={selectedSecondLevelClass}
                    objectData={objectData}
                    token={token}
                    selectedPhoto={selectedPhoto}
                    selectedUserBox={coordinatesForPropertiesPanel}
                    selectedBoxInfo={selectedBox}
                    annotations={annotations}
                    additionalAnnotations={additionalAnnotations}
                    apiDataForPropertiesPanel={apiDataForPropertiesPanel}
                    onFirstLevelChange={setSelectedFirstLevelClass}
                    onSecondLevelChange={setSelectedSecondLevelClass}
                    onSaveSuccess={async (
                      newFirstClass,
                      newSecondClass,
                      isNew,
                      boxId,
                    ) => {
                      if (isNew && boxId) {
                        const numericId =
                          typeof boxId === "string"
                            ? Number(boxId.replace("saved-", ""))
                            : boxId;
                        setSavedBoxes((prev) =>
                          prev.filter((b) => b.id !== numericId),
                        );
                      }

                      setSelectedBox(null);
                      setCoordinatesForPropertiesPanel(null);
                      setApiDataForPropertiesPanel(null);

                      if (newFirstClass)
                        setSelectedFirstLevelClass(newFirstClass);
                      if (newSecondClass)
                        setSelectedSecondLevelClass(newSecondClass);

                      if (newFirstClass) {
                        setExpandedItems((prev) =>
                          new Set(prev).add(newFirstClass),
                        );
                      }

                      setRefreshTrigger((prev) => prev + 1);

                      if (selectedPhoto) {
                        setAnnotationsLoading(true);
                        await Promise.all([
                          loadAnnotations(
                            selectedPhoto,
                            newFirstClass || selectedFirstLevelClass,
                            newSecondClass || null,
                          ),
                          loadAdditionalAnnotations(),
                        ]);
                        setAnnotationsLoading(false);
                      }
                    }}
                    onRequestOpenSettings={handleRequestOpenSettings}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              flex: "1",
              borderRight: "1px solid #dee2e6",
              backgroundColor: "#f9f9f9",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* ДОБАВЛЕН zoomContainerRef СЮДА ДЛЯ РАБОТЫ СКРОЛЛА */}
            <div
              ref={zoomContainerRef}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto", // Изменено с hidden на auto для поддержки скроллбаров при зуме
                position: "relative",
              }}
            >
              {fullImageLoading ? (
                <div style={{ textAlign: "center" }}>
                  <Spinner />
                  <div
                    style={{
                      marginTop: "10px",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Загрузка изображения...
                  </div>
                </div>
              ) : fullImageSrc ? (
                <div
                  id="image-container"
                  style={{
                    position: "relative",
                    display: "inline-block",
                    // ДОБАВЛЕН SCALE В TRANSFORM
                    transform: `rotate(${rotation}deg) scale(${scale})`,
                    transformOrigin: "center center",
                    transition:
                      isDrawing || draggingBox || resizingBox
                        ? "none"
                        : "transform 0.1s ease",
                    userSelect: "none",
                    cursor: isDrawingMode ? "crosshair" : "default",
                  }}
                  onMouseDown={handleMouseDown}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <img
                    ref={imageRef}
                    src={fullImageSrc}
                    alt="Полный размер"
                    onLoad={handleImageLoad}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />
                  {!fullImageLoading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                      }}
                    >
                      {renderAnnotations(annotations, "#ff0000", "Tree")}
                      {renderAnnotations(
                        filteredAdditionalAnnotations,
                        "#00ff00",
                        "Detected",
                      )}
                      {renderAnnotations(savedBoxes, markerColor, "User")}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          pointerEvents: "none",
                          zIndex: 15,
                        }}
                      >
                        {selectedBox && <ResizeHandles box={selectedBox} />}
                      </div>
                    </div>
                  )}
                  {tempBox && (
                    <div
                      style={getBoxStyle(tempBox)}
                      onClick={(e) => e.stopPropagation()}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "-20px",
                          left: "0",
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: isDarkColor(markerColor) ? "white" : "black",
                          whiteSpace: "nowrap",
                          background: markerColor,
                          padding: "2px 4px",
                          borderRadius: "2px",
                        }}
                      >
                        {currentDefectName}
                      </div>
                      <CButton
                        size="sm"
                        color="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTempBox(null);
                          setIsDrawing(false);
                        }}
                        style={{
                          position: "absolute",
                          top: "-10px",
                          right: "-10px",
                          width: "24px",
                          height: "24px",
                          padding: 0,
                          borderRadius: "50%",
                          minWidth: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#d9534f",
                          border: "2px solid white",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                          transition: "background 0.2s",
                          cursor: "pointer",
                          zIndex: 11,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#c9302c")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#d9534f")
                        }
                      >
                        <CIcon
                          icon={cilTrash}
                          style={{
                            color: "white",
                            width: "14px",
                            height: "14px",
                          }}
                        />
                      </CButton>
                    </div>
                  )}
                </div>
              ) : selectedPhoto ? (
                <div style={{ color: "#999", fontSize: "13px" }}>
                  Ошибка загрузки
                </div>
              ) : (
                <div style={{ color: "#999", fontSize: "13px" }}>
                  Выберите фото
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              width: "280px",
              borderLeft: "1px solid #dee2e6",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                backgroundColor: "#fdfdfd",
                padding: "4px 0",
              }}
            >
              {loading ? (
                <div style={{ padding: "20px 10px" }}>
                  <CProgress height="4px" style={{ marginBottom: "10px" }}>
                    <CProgressBar
                      color="info"
                      role="progressbar"
                      style={{ width: "0%" }}
                    />
                  </CProgress>
                  <Spinner />
                </div>
              ) : photos.length === 0 ? (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#999",
                    fontSize: "12px",
                  }}
                >
                  Нет фото
                </div>
              ) : (
                photos.map((photo, index) => {
                  const isSelected = selectedPhoto?.path === photo.path;
                  return (
                    <div
                      key={`${photo.path}-${index}`}
                      style={{
                        marginBottom: "8px",
                        borderBottom: "1px solid #eee",
                        backgroundColor: "white",
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "200px",
                          height: "150px",
                          margin: "0 auto",
                          overflow: "hidden",
                          borderRadius: "6px",
                          border: isSelected
                            ? "2px solid #0d6efd"
                            : "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}
                        onClick={() => handleThumbnailClick(photo)}
                      >
                        <img
                          src={photo.url}
                          alt={photo.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            cursor: "pointer",
                          }}
                          onError={(e) => {
                            e.target.style.backgroundColor = "#f0f0f0";
                            e.target.textContent = "Ошибка";
                          }}
                        />
                        {fullImageLoading && isSelected && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Spinner />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "11px",
                          color: "#555",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          padding: "0 4px",
                        }}
                        title={photo.name}
                      >
                        {photo.name}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </CModalBody>
      </CModal>
      <SettingsNeuralModule
        visible={settingsModalVisible}
        onClose={closeSettingsModal}
        objectData={objectData}
        filterAiObjectID={
          settingsMode === "filter" ? objectData?.AiObjectID || null : null
        }
      />
      <AddDefectsModal
        visible={addDefectsModalVisible}
        onClose={handleAddDefectsModalClose}
        objectData={objectData}
      />
      <ReportNeuralModule
        visible={showReportModal}
        onClose={() => setShowReportModal(false)}
        objectData={objectData}
        objectId={objectData?.Id}
      />
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default MainNeuralModal;